/**
 * Öffentliche Handy-Scan-Seite (Live-Handoff, Multi-Dokument): /m/scan/:token
 *
 * Jun-2026 (User-Wunsch) — Mitarbeiter scannt den QR am Theken-PC mit dem Handy
 * (KEIN Login nötig) und fotografiert nacheinander mehrere Belege:
 * ZB1/COC, Ausweis/Führerschein, alte Rechnung/Auftrag, Schadensfotos. Mit
 * „Fertig" landen alle Belege + der erkannte Kunde/Fahrzeug sofort am Theken-PC.
 */
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertCircle, Plus, Send, FileText, IdCard, ReceiptText, Camera, MapPin, KeyRound, Check, Phone, Mail, Gauge, Clock, ScanLine, History } from "lucide-react";
import Logo from "../components/Logo";
import EanCameraScanner from "../components/wms/EanCameraScanner";
import { VinWarning } from "../components/common/VinWarning";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
    zb1: "Fahrzeugpapiere", coc: "COC", id: "Ausweispapiere",
    invoice: "Rechnung / Auftrag / Versicherungsschein", damage: "Schadensfoto",
    standort: "Abstellort-Foto", schluesseleinwurf: "Foto Schlüsseleinwurf", vehicle: "Fahrzeugfoto",
    kmstand: "Foto KM-Stand",
};
const _today = () => new Date().toISOString().slice(0, 10);
const fmtDateTime = (d) => {
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}, ${p(d.getHours())}:${p(d.getMinutes())} Uhr`;
};

export default function MobileScanPage() {
    const { token } = useParams();
    const [state, setState] = useState("checking"); // checking | ready | done | invalid
    const [purpose, setPurpose] = useState("workshop"); // workshop | intake
    const [intakeResult, setIntakeResult] = useState(null);
    const [docs, setDocs] = useState([]); // {kind, document_type, name}
    const [uploadingKind, setUploadingKind] = useState(null); // "zb1" | "invoice" | "id-front" | ...
    const [completing, setCompleting] = useState(false);
    const [error, setError] = useState("");
    const [match, setMatch] = useState(null);              // {customer, vehicle, source}
    const [outsideHours, setOutsideHours] = useState(false);
    const [contactConfirmed, setContactConfirmed] = useState(false);
    const [idSides, setIdSides] = useState({ front: false, back: false });
    const [revealId, setRevealId] = useState(false);
    const [revealManual, setRevealManual] = useState(false);
    const [intakeMode, setIntakeMode] = useState(null); // null | fahrzeugpapiere | versicherung | ausweis | fin
    const [scanOpen, setScanOpen] = useState(false); // Kamera-Barcode-Scanner (FIN) offen
    const [intakePrecheck, setIntakePrecheck] = useState(null); // {found, vehicle, duplicate}
    const [openedAt] = useState(() => new Date()); // Annahme-Zeitstempel (read-only)
    const [form, setForm] = useState({
        customer_name: "", phone: "", email: "", license_plate: "", vin: "", key_number: "",
        appointment_date: _today(), appointment_daypart: "", parking_location: "", requested_work: "",
    });
    const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
    const fileRef = useRef(null);
    const pendingKind = useRef("zb1");
    const pendingSide = useRef(null);
    const pendingLabel = useRef(null);
    // Jul-2026 (User-Wunsch) — ZB1 Vorder-/Rückseite + bis zu 8 vorbeschriftete Fotos.
    const [zb1Sides, setZb1Sides] = useState({ front: false, back: false });
    const [photoDone, setPhotoDone] = useState({});
    const [dealerBrand, setDealerBrand] = useState({ logo: "", name: "" });
    const PHOTO_SLOTS = ["TACHO", "VORNE", "LINKS", "RECHTS", "HINTEN", "OBEN", "SCHADEN 1", "SCHADEN 2"];

    const applyMatch = (m) => {
        if (!m) return;
        setMatch(m);
        setForm((p) => ({
            ...p,
            email: p.email || m.customer?.email || "",
            phone: p.phone || m.customer?.phone || "",
            customer_name: p.customer_name || m.customer?.label || "",
            license_plate: p.license_plate || m.vehicle?.license_plate || "",
            vin: p.vin || m.vehicle?.vin || "",
        }));
    };

    useEffect(() => {
        let cancel = false;
        axios.get(`${API}/public/scan-sessions/${token}`)
            .then(({ data }) => {
                if (cancel) return;
                if (!data.valid) setState("invalid");
                else if (data.status === "complete" || data.status === "consumed") setState("done");
                else {
                    setDocs(data.docs || []);
                    setPurpose(data.purpose || "workshop");
                    setOutsideHours(!!data.outside_hours);
                    setDealerBrand({ logo: data.dealer_logo || "", name: data.dealer_name || "" });
                    if (data.match) applyMatch(data.match);
                    setState("ready");
                }
            })
            .catch(() => { if (!cancel) setState("invalid"); });
        return () => { cancel = true; };
    }, [token]);

    // Live-Dublettenwarnung beim FIN-Eingang (Scan/Tippen) im Intake-Modus:
    // erkennt Bestandsfahrzeug + warnt, falls bereits an anderem Standort eingegangen.
    useEffect(() => {
        if (purpose !== "intake") return undefined;
        const fin = (form.vin || "").replace(/[\s-]/g, "");
        if (fin.length < 5) { setIntakePrecheck(null); return undefined; }
        const t = setTimeout(async () => {
            try {
                const { data } = await axios.post(`${API}/public/scan-sessions/${token}/intake-precheck`, { vin: fin });
                setIntakePrecheck(data);
            } catch { setIntakePrecheck(null); }
        }, 600);
        return () => clearTimeout(t);
    }, [form.vin, purpose, token]);

    const pick = (kind, side = null, label = null) => {
        pendingKind.current = kind;
        pendingSide.current = side;
        pendingLabel.current = label;
        setError("");
        fileRef.current?.click();
    };

    const onFile = async (file) => {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { setError("Datei zu groß (max 10 MB)"); return; }
        const kind = pendingKind.current;
        const side = pendingSide.current;
        const label = pendingLabel.current;
        setError("");
        setUploadingKind(label ? `photo-${label}` : (side ? `${kind}-${side}` : kind));
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("kind", kind);
            if (label) fd.append("label", label);
            const { data } = await axios.post(`${API}/public/scan-sessions/${token}/upload`, fd);
            setDocs((prev) => [...prev, { kind, document_type: data.document_type, name: file.name, label }]);
            if (side && kind === "id") setIdSides((p) => ({ ...p, [side]: true }));
            if (side && kind === "zb1") setZb1Sides((p) => ({ ...p, [side]: true }));
            if (label) setPhotoDone((p) => ({ ...p, [label]: true }));
            if (data.match) applyMatch(data.match);
        } catch (e) {
            setError(e.response?.data?.detail || "Upload fehlgeschlagen");
        } finally {
            setUploadingKind(null);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const finish = async () => {
        setCompleting(true);
        try {
            const hasForm = Object.values(form).some((v) => (v || "").trim());
            const intakeForm = { vin: form.vin, key_number: form.key_number };
            const { data } = await axios.post(`${API}/public/scan-sessions/${token}/complete`,
                { form: purpose === "intake" ? intakeForm : (purpose === "papers" ? null : (hasForm ? form : null)) });
            if (data?.intake) setIntakeResult(data.intake);
            setState("done");
        } catch (e) {
            setError(e.response?.data?.detail || "Senden fehlgeschlagen");
            setCompleting(false);
        }
    };

    const canFinish = purpose === "intake"
        ? (docs.length > 0 || (form.vin || "").replace(/[\s-]/g, "").length >= 5)
        : purpose === "papers"
        ? zb1Sides.front
        : docs.length > 0 || Object.values(form).some((v) => (v || "").trim());

    // Geführter Werkstatt-Flow (User-Wunsch): ZB1+Rechnung → sonst Ausweis → sonst manuell.
    const step1Docs = docs.filter((d) => d.kind === "zb1" || d.kind === "invoice").length;
    const idDocs = docs.filter((d) => d.kind === "id").length;
    const identDocs = step1Docs + idDocs; // identifizierende Belege (ZB1/Rechnung/Ausweis)
    // Ausweispapiere werden über den Wizard-Modus „ausweis" angeboten.
    // Jun-2026 (User-Wunsch/Bugfix): Konnte trotz gescanntem Beleg KEIN Kunde/Fahrzeug
    // zugeordnet werden, wird die manuelle Schnellerfassung AUTOMATISCH geöffnet
    // (vorher Sackgasse: es gab keinen Button, um die Erfassung zu starten).
    const noMatchAfterScan = identDocs > 0 && !match?.customer && !uploadingKind;
    const showManual = (step1Docs === 0 && idDocs === 0 && revealManual) || noMatchAfterScan;
    const foundCustomer = match?.customer;
    const foundVehicle = match?.vehicle;
    // Aug-2026 (User-Wunsch): FIN fehlt nach Papier-Scan → rot animierte Nacheingabe
    const zb1Scanned = docs.some((d) => d.kind === "zb1");
    const vinMissing = purpose !== "intake" && zb1Scanned && !uploadingKind
        && !(match?.vehicle?.vin) && !(form.vin || "").replace(/[\s-]/g, "").trim();

    return (
        <div className="min-h-screen bg-[#111827] text-white flex flex-col items-center px-5 py-8" data-testid="mobile-scan-page">
            <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment"
                onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} className="hidden" data-testid="mscan-file-input" />

            <div className="w-full max-w-sm">
                <div className="flex justify-center mb-6">
                    {dealerBrand.logo ? (
                        <img
                            src={dealerBrand.logo}
                            alt={dealerBrand.name || "Händlerlogo"}
                            className="max-h-12 max-w-[220px] object-contain"
                            data-testid="mscan-dealer-logo"
                        />
                    ) : (
                        <Logo className="inline-flex items-center gap-2 text-white" boxClass="w-9 h-9" iconClass="w-5 h-5" textClass="text-xl" deClass="text-white/50" />
                    )}
                </div>

                {state === "checking" && (
                    <div className="text-center text-white/70 flex flex-col items-center gap-3 mt-10" data-testid="mscan-checking">
                        <Loader2 className="w-7 h-7 animate-spin" /> Prüfe Link…
                    </div>
                )}

                {state === "invalid" && (
                    <div className="text-center bg-red-500/10 border border-red-500/40 rounded-sm px-4 py-6 mt-10" data-testid="mscan-invalid">
                        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                        <div className="font-bold">Link ungültig oder abgelaufen</div>
                        <p className="text-sm text-white/60 mt-1">Bitte am Theken-PC einen neuen QR-Code anzeigen lassen.</p>
                    </div>
                )}

                {/* Jul-2026 (User-Wunsch) — Nur Fahrzeugpapiere/ZB1 (Vorder-/Rückseite)
                    + bis zu 8 vorbeschriftete Fotos. Ermittelt die Fahrzeugdaten. */}
                {state === "ready" && purpose === "papers" && (
                    <div data-testid="mscan-papers-ready">
                        <h1 className="font-display text-2xl font-black mb-1 text-center">Fahrzeugpapiere scannen</h1>
                        <p className="text-sm text-white/60 mb-5 text-center">
                            Fotografiere den <b>Fahrzeugschein (ZB1)</b> — die Fahrzeugdaten werden automatisch am PC übernommen. Zusätzlich kannst du bis zu 8 Fahrzeugfotos aufnehmen.
                        </p>

                        <div className="bg-white/5 border border-white/10 rounded-sm p-3.5 mb-4">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-[#FFCC00] mb-2">Fahrzeugschein (ZB1) — Vorderseite (Pflicht) &amp; Rückseite (optional)</div>
                            <div className="grid grid-cols-2 gap-2">
                                {[{ side: "front", label: "Vorderseite", req: true }, { side: "back", label: "Rückseite", req: false }].map(({ side, label, req }) => {
                                    const busy = uploadingKind === `zb1-${side}`;
                                    const done = zb1Sides[side];
                                    return (
                                        <button key={side} type="button" onClick={() => pick("zb1", side)} disabled={!!uploadingKind}
                                            className={`flex flex-col items-center justify-center gap-1 rounded-sm border-2 py-4 text-sm font-bold transition-colors ${done ? "border-emerald-400 bg-emerald-400/10 text-emerald-300" : "border-white/20 bg-white/5 hover:border-[#FFCC00]"}`}
                                            data-testid={`mscan-papers-zb1-${side}`}>
                                            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : done ? <Check className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                                            {label}{req && !done ? " *" : ""}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {(foundVehicle || match?.vehicle) && (
                            <div className="bg-emerald-400/10 border border-emerald-400/40 rounded-sm px-3 py-2 mb-4 text-sm" data-testid="mscan-papers-vehicle">
                                <CheckCircle2 className="w-4 h-4 text-emerald-400 inline mr-1" />
                                <b>{[(foundVehicle || match?.vehicle)?.brand, (foundVehicle || match?.vehicle)?.model].filter(Boolean).join(" ") || "Fahrzeug erkannt"}</b>
                            </div>
                        )}

                        <div className="bg-white/5 border border-white/10 rounded-sm p-3.5 mb-4">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-[#FFCC00] mb-2">Fahrzeugfotos (optional, max. 8)</div>
                            <div className="grid grid-cols-2 gap-2">
                                {PHOTO_SLOTS.map((slot) => {
                                    const busy = uploadingKind === `photo-${slot}`;
                                    const done = photoDone[slot];
                                    return (
                                        <button key={slot} type="button" onClick={() => pick("photo", null, slot)} disabled={!!uploadingKind}
                                            className={`flex items-center justify-center gap-1.5 rounded-sm border-2 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${done ? "border-emerald-400 bg-emerald-400/10 text-emerald-300" : "border-white/20 bg-white/5 hover:border-[#FFCC00]"}`}
                                            data-testid={`mscan-papers-photo-${slot.replace(/\s+/g, "-").toLowerCase()}`}>
                                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : done ? <Check className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
                                            {slot}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {error && <div className="text-red-400 text-sm mb-3 text-center" data-testid="mscan-papers-error">{error}</div>}

                        <button type="button" onClick={finish} disabled={!canFinish || completing || !!uploadingKind}
                            className="w-full bg-[#FFCC00] text-[#111827] font-black rounded-sm py-3.5 uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-40"
                            data-testid="mscan-papers-finish">
                            {completing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Fertig — Daten an PC senden
                        </button>
                        <p className="text-[11px] text-white/40 mt-2 text-center">* Vorderseite des Fahrzeugscheins ist erforderlich.</p>
                    </div>
                )}


                {state === "ready" && purpose === "intake" && (
                    <div data-testid="mscan-intake-ready">
                        <h1 className="font-display text-2xl font-black mb-1 text-center">Fahrzeug-Eingang</h1>
                        <p className="text-sm text-white/60 mb-5 text-center">
                            <b>FIN scannen oder eintippen</b> (die letzten 8 Stellen genügen). Das Fahrzeug wird erkannt,
                            dem Standort zugewiesen und du vergibst eine Schlüsselnummer.
                        </p>

                        {/* FIN: scannen (QR/EAN/Code) ODER manuell — ZB1 ist nicht mehr Pflicht */}
                        <div className="bg-white/5 border border-white/10 rounded-sm p-3.5" data-testid="mscan-intake-fin-box">
                            <label htmlFor="intake-fin" className="text-[10px] uppercase tracking-widest font-bold text-[#FFCC00]">Fahrgestellnummer (FIN)</label>
                            <div className="flex gap-2 mt-1.5">
                                <input id="intake-fin" value={form.vin} onChange={setF("vin")} placeholder="FIN oder letzte 8 Stellen…"
                                    className="flex-1 bg-[#1f2937] border border-white/15 rounded-sm px-3 py-2.5 text-sm uppercase tracking-wide outline-none focus:border-[#FFCC00]"
                                    autoCapitalize="characters" autoCorrect="off" spellCheck={false} data-testid="mscan-intake-vin" />
                                <button type="button" onClick={() => { setError(""); setScanOpen(true); }}
                                    className="shrink-0 bg-[#FFCC00] text-[#111827] font-bold rounded-sm px-3 flex items-center gap-1.5 text-xs active:scale-95 transition-transform"
                                    data-testid="mscan-intake-scan-fin">
                                    <ScanLine className="w-4 h-4" /> scannen
                                </button>
                            </div>
                            <VinWarning value={form.vin} allowShortCode />
                            {intakePrecheck?.found && intakePrecheck.duplicate && (
                                <div className="mt-2.5 text-[12px] text-red-100 bg-red-600/25 border border-red-500/60 rounded-sm px-3 py-2.5 flex items-start gap-2" data-testid="mscan-intake-duplicate-warning">
                                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-300" />
                                    <span><b>Achtung – mögliche Doppelannahme!</b> Dieses Fahrzeug ist bereits eingegangen
                                        {intakePrecheck.duplicate.branch_label ? <> an <b>{intakePrecheck.duplicate.branch_label}</b></> : " an einem anderen Standort"}
                                        {intakePrecheck.duplicate.arrived_at ? <> (am {fmtDateTime(new Date(intakePrecheck.duplicate.arrived_at))})</> : null}. Bitte vor dem Erfassen prüfen.</span>
                                </div>
                            )}
                            {intakePrecheck?.found && !intakePrecheck.duplicate && (
                                <div className="mt-2.5 text-[12px] text-emerald-100 bg-emerald-600/20 border border-emerald-500/50 rounded-sm px-3 py-2 flex items-center gap-2" data-testid="mscan-intake-precheck-ok">
                                    <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-300" />
                                    <span>Erkannt: <b>{[intakePrecheck.vehicle?.brand, intakePrecheck.vehicle?.model].filter(Boolean).join(" ") || "Fahrzeug"}</b></span>
                                </div>
                            )}
                        </div>

                        {/* Schlüsselnummer manuell vergeben (User-Wunsch) */}
                        <div className="mt-2.5 bg-white/5 border border-white/10 rounded-sm p-3.5" data-testid="mscan-intake-key-box">
                            <label htmlFor="intake-key" className="text-[10px] uppercase tracking-widest font-bold text-[#FFCC00] inline-flex items-center gap-1.5">
                                <KeyRound className="w-3.5 h-3.5" /> Schlüsselnummer
                            </label>
                            <input id="intake-key" value={form.key_number} onChange={setF("key_number")} placeholder="z. B. Brett-Nr. / Hakennummer"
                                className="mt-1.5 w-full bg-[#1f2937] border border-white/15 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-[#FFCC00]"
                                data-testid="mscan-intake-keynumber" />
                            <p className="text-[11px] text-white/40 mt-1.5">Leer lassen → es werden automatisch die letzten 2 FIN-Stellen als Schlüsseldepot genutzt.</p>
                        </div>

                        {/* Optional: ZB1 + Fahrzeugfoto archivieren */}
                        <div className="mt-2.5 grid grid-cols-2 gap-2.5">
                            {[
                                { kind: "zb1", label: "Fahrzeugschein (ZB1)", icon: FileText, hint: "optional" },
                                { kind: "vehicle", label: "Fahrzeugfoto", icon: Camera, hint: "optional" },
                            ].map(({ kind, label, icon: Icon, hint }) => {
                                const count = docs.filter((d) => d.kind === kind).length;
                                const busy = uploadingKind === kind;
                                return (
                                    <button key={kind} type="button" onClick={() => pick(kind)} disabled={!!uploadingKind}
                                        className="bg-white/5 hover:bg-white/10 border border-white/15 rounded-sm px-3 py-3 flex flex-col items-center gap-1 active:scale-[0.98] transition-transform disabled:opacity-50"
                                        data-testid={`mscan-intake-${kind}`}>
                                        {busy ? <Loader2 className="w-5 h-5 text-[#FFCC00] animate-spin" /> : count > 0 ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <Icon className="w-5 h-5 text-[#FFCC00]" />}
                                        <span className="text-[11px] font-bold text-center leading-tight">{label}</span>
                                        <span className="text-[9px] text-white/40">{count > 0 ? `${count} erfasst` : hint}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {error && <div className="mt-4 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2" data-testid="mscan-error">{error}</div>}
                        <button type="button" onClick={finish} disabled={!canFinish || completing || !!uploadingKind}
                            className="mt-6 w-full bg-[#FFCC00] disabled:bg-white/15 disabled:text-white/40 text-[#111827] font-black uppercase tracking-wider rounded-sm py-4 active:scale-95 transition-transform inline-flex items-center justify-center gap-2"
                            data-testid="mscan-intake-finish">
                            {completing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                            Fahrzeug erfassen
                        </button>

                        {scanOpen && (
                            <EanCameraScanner
                                onDetected={(code) => {
                                    const clean = (code || "").toUpperCase().replace(/[\s-]/g, "");
                                    setForm((p) => ({ ...p, vin: clean }));
                                    setScanOpen(false);
                                }}
                                onClose={() => setScanOpen(false)}
                            />
                        )}
                    </div>
                )}

                {state === "ready" && purpose !== "intake" && purpose !== "papers" && (
                    <div data-testid="mscan-ready">
                        <h1 className="font-display text-2xl font-black mb-1 text-center">{purpose === "tradein" ? "Inzahlungnahme · Dokumente" : "Annahme · Auftrag eröffnen"}</h1>
                        {purpose === "tradein" && (
                            <p className="text-center text-white/70 text-sm mb-3" data-testid="mscan-tradein-intro">Fotografieren Sie den Fahrzeugschein (ZB1){", Ausweis und Belege"}. Die Daten erscheinen sofort am Theken-PC.</p>
                        )}

                        {/* Auftrags-Datum & -Uhrzeit: automatisch erfasst, schreibgeschützt */}
                        <div className="mb-4 bg-white/5 border border-white/10 rounded-sm px-3 py-2.5 flex items-center gap-2" data-testid="mscan-opened-at">
                            <Clock className="w-4 h-4 text-[#FFCC00] shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] uppercase tracking-wider font-bold text-white/50">Angenommen am (automatisch)</div>
                                <div className="text-sm font-bold font-mono-num" data-testid="mscan-opened-at-value">{fmtDateTime(openedAt)}</div>
                            </div>
                            <span className="text-[9px] uppercase tracking-wider font-bold text-white/30 border border-white/15 rounded-sm px-1.5 py-0.5">schreibgeschützt</span>
                        </div>

                        {!intakeMode && (
                            <div data-testid="mscan-intake-chooser">
                                <div className="text-center font-display text-lg font-black mb-1">{purpose === "tradein" ? "Was möchten Sie fotografieren?" : "Wie möchten Sie den Auftrag eröffnen?"}</div>
                                <p className="text-sm text-white/55 mb-4 text-center">Wählen Sie eine Methode — Sie scannen danach nur den gewählten Beleg.</p>
                                <div className="space-y-2.5">
                                    {[
                                        { mode: "fahrzeugpapiere", label: "Fahrzeugpapiere", icon: FileText, hint: "Fahrzeugschein scannen — FIN wird automatisch ausgelesen" },
                                        { mode: "versicherung", label: "Versicherungsschein oder alter Auftrag / Rechnung", icon: ReceiptText, hint: "Kunde & Arbeiten werden automatisch erkannt" },
                                        { mode: "ausweis", label: "Ausweispapiere", icon: IdCard, hint: "Vorder- & Rückseite — Name und Adresse werden erkannt" },
                                        { mode: "fin", label: "Fahrgestellnummer (FIN) am Fahrzeug", icon: Camera, hint: "FIN scannen oder eintippen" },
                                    ].map(({ mode, label, icon: Icon, hint }) => (
                                        <button key={mode} type="button" onClick={() => setIntakeMode(mode)}
                                            className="w-full bg-white/5 hover:bg-white/10 border border-white/15 rounded-sm px-4 py-3.5 flex items-center gap-3 text-left active:scale-[0.98] transition-transform"
                                            data-testid={`mscan-open-${mode}`}>
                                            <div className="w-10 h-10 rounded-sm bg-[#FFCC00]/15 flex items-center justify-center shrink-0">
                                                <Icon className="w-5 h-5 text-[#FFCC00]" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-sm">{label}</div>
                                                <div className="text-[11px] text-white/50">{hint}</div>
                                            </div>
                                            <Plus className="w-4 h-4 text-white/40 shrink-0" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {intakeMode && (
                            <>
                                <button type="button" onClick={() => setIntakeMode(null)} disabled={!!uploadingKind}
                                    className="mb-3 text-xs text-white/60 hover:text-white underline disabled:opacity-40" data-testid="mscan-change-method">
                                    ← Andere Methode wählen
                                </button>

                                {intakeMode === "fahrzeugpapiere" && (
                                    <div className="bg-white/5 border border-white/10 rounded-sm p-3" data-testid="mscan-mode-fahrzeugpapiere">
                                        <button type="button" onClick={() => pick("zb1")} disabled={!!uploadingKind}
                                            className="w-full bg-white/5 hover:bg-white/10 border border-white/15 rounded-sm px-4 py-3 flex items-center gap-3 text-left active:scale-[0.98] transition-transform disabled:opacity-50"
                                            data-testid="mscan-kind-zb1">
                                            <div className="w-9 h-9 rounded-sm bg-[#FFCC00]/15 flex items-center justify-center shrink-0">
                                                {uploadingKind === "zb1" ? <Loader2 className="w-5 h-5 text-[#FFCC00] animate-spin" /> : <FileText className="w-5 h-5 text-[#FFCC00]" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-sm flex items-center gap-2">Fahrzeugpapiere scannen
                                                    {docs.filter((d) => d.kind === "zb1").length > 0 && <span className="text-[10px] bg-emerald-500 text-white rounded-full px-1.5 py-0.5 font-black">{docs.filter((d) => d.kind === "zb1").length}</span>}
                                                </div>
                                                <div className="text-[11px] text-white/50 truncate">{uploadingKind === "zb1" ? "KI analysiert…" : "FIN wird automatisch ausgelesen"}</div>
                                            </div>
                                            <Plus className="w-4 h-4 text-white/40 shrink-0" />
                                        </button>
                                    </div>
                                )}

                                {intakeMode === "versicherung" && (
                                    <div className="bg-white/5 border border-white/10 rounded-sm p-3" data-testid="mscan-mode-versicherung">
                                        <button type="button" onClick={() => pick("invoice")} disabled={!!uploadingKind}
                                            className="w-full bg-white/5 hover:bg-white/10 border border-white/15 rounded-sm px-4 py-3 flex items-center gap-3 text-left active:scale-[0.98] transition-transform disabled:opacity-50"
                                            data-testid="mscan-kind-invoice">
                                            <div className="w-9 h-9 rounded-sm bg-[#FFCC00]/15 flex items-center justify-center shrink-0">
                                                {uploadingKind === "invoice" ? <Loader2 className="w-5 h-5 text-[#FFCC00] animate-spin" /> : <ReceiptText className="w-5 h-5 text-[#FFCC00]" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-sm flex items-center gap-2">Versicherungsschein / alter Auftrag / Rechnung
                                                    {docs.filter((d) => d.kind === "invoice").length > 0 && <span className="text-[10px] bg-emerald-500 text-white rounded-full px-1.5 py-0.5 font-black">{docs.filter((d) => d.kind === "invoice").length}</span>}
                                                </div>
                                                <div className="text-[11px] text-white/50 truncate">{uploadingKind === "invoice" ? "KI analysiert…" : "Kunde & Arbeiten werden erkannt"}</div>
                                            </div>
                                            <Plus className="w-4 h-4 text-white/40 shrink-0" />
                                        </button>
                                    </div>
                                )}

                                {intakeMode === "fin" && (
                                    <div className="bg-white/5 border border-white/10 rounded-sm p-3" data-testid="mscan-mode-fin">
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-white/50 mb-1.5">Fahrgestellnummer (FIN) am Fahrzeug</div>
                                        <div className="flex gap-2">
                                            <input value={form.vin} onChange={setF("vin")} placeholder="FIN eintragen…" className="flex-1 bg-[#1f2937] border border-white/15 rounded-sm px-3 py-2.5 text-sm uppercase" data-testid="mscan-id-vin" />
                                            <button type="button" onClick={() => pick("zb1")} disabled={!!uploadingKind}
                                                className="shrink-0 bg-white/5 hover:bg-white/10 border border-white/15 rounded-sm px-3 flex items-center gap-1.5 text-xs font-bold disabled:opacity-50" data-testid="mscan-scan-vin">
                                                <Camera className="w-4 h-4 text-[#FFCC00]" /> scannen
                                            </button>
                                        </div>
                                    </div>
                                )}

                        {/* Ausweispapiere (beide Seiten) */}
                        {intakeMode === "ausweis" && (
                            <div className="mt-3 bg-white/5 border border-white/10 rounded-sm p-3" data-testid="mscan-step2">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-[#FFCC00] mb-2">Ausweispapiere — beide Seiten (Pflicht: Vorder- &amp; Rückseite)</div>
                                <div className="grid grid-cols-2 gap-2.5">
                                    {[{ side: "front", label: "Vorderseite" }, { side: "back", label: "Rückseite" }].map(({ side, label }) => {
                                        const busy = uploadingKind === `id-${side}`;
                                        const done = idSides[side];
                                        return (
                                            <button key={side} type="button" onClick={() => pick("id", side)} disabled={!!uploadingKind}
                                                className="bg-white/5 hover:bg-white/10 border border-white/15 rounded-sm px-3 py-3 flex flex-col items-center gap-1 active:scale-[0.98] transition-transform disabled:opacity-50"
                                                data-testid={`mscan-id-${side}`}>
                                                {busy ? <Loader2 className="w-5 h-5 text-[#FFCC00] animate-spin" /> : done ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <IdCard className="w-5 h-5 text-[#FFCC00]" />}
                                                <span className="text-xs font-bold">{label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                                {idDocs > 0 && !(idSides.front && idSides.back) && (
                                    <div className="mt-2.5 text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-sm px-2.5 py-2 flex items-start gap-1.5" data-testid="mscan-id-incomplete-hint">
                                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                        <span>Bitte <b>beide Ausweisseiten</b> scannen — der Name steht auf der Vorderseite, die Adresse auf der Rückseite.</span>
                                    </div>
                                )}
                                {idDocs === 0 && !showManual && (
                                    <button type="button" onClick={() => setRevealManual(true)} className="mt-2.5 text-xs text-white/50 underline" data-testid="mscan-no-id">
                                        Kein Ausweis vorhanden? — manuell erfassen
                                    </button>
                                )}
                            </div>
                        )}

                        {error && <div className="mt-4 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2" data-testid="mscan-error">{error}</div>}

                        {/* Aug-2026 (User-Wunsch): FIN nicht erkannt → rot pulsierende Nacheingabe */}
                        {vinMissing && (
                            <div className="mt-4 bg-red-500/15 border-2 border-red-500 rounded-sm p-4 animate-pulse" data-testid="mscan-vin-missing">
                                <div className="flex items-center gap-2 mb-2">
                                    <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
                                    <div className="text-sm font-bold text-red-300 uppercase tracking-wide">Fahrgestellnummer nicht erkannt</div>
                                </div>
                                <p className="text-[11px] text-red-200/80 mb-2">Die FIN konnte aus den Papieren nicht ausgelesen werden — bitte hier eintippen:</p>
                                <input
                                    value={form.vin}
                                    onChange={setF("vin")}
                                    placeholder="FIN eintippen…"
                                    className="w-full bg-[#1f2937] border border-red-400 rounded-sm px-3 py-2.5 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-red-500"
                                    autoCapitalize="characters" autoCorrect="off" spellCheck={false}
                                    data-testid="mscan-vin-manual-input"
                                />
                            </div>
                        )}

                        {/* Fahrzeug erkannt (z. B. eigener Bestand via FIN) */}
                        {foundVehicle && (
                            <div className="mt-4 bg-sky-500/10 border border-sky-500/40 rounded-sm p-4" data-testid="mscan-vehicle-found">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-5 h-5 text-sky-300" />
                                    <div className="font-bold text-sm">
                                        Fahrzeug erkannt{foundVehicle.from_inventory ? " (Bestand)" : ""}: {[foundVehicle.brand, foundVehicle.model].filter(Boolean).join(" ") || "Fahrzeug"}
                                    </div>
                                </div>
                                <div className="text-[11px] text-white/60 mt-1">
                                    {foundVehicle.vin ? `FIN ${foundVehicle.vin}` : ""}{foundVehicle.license_plate ? ` · ${foundVehicle.license_plate}` : ""}
                                </div>
                            </div>
                        )}

                        {/* Kunde erkannt → Kontaktdaten bestätigen */}
                        {foundCustomer && (
                            <div className="mt-4 bg-emerald-500/10 border border-emerald-500/40 rounded-sm p-4" data-testid="mscan-customer-found">
                                <div className="flex items-center gap-2 mb-2">
                                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                    <div className="font-bold text-sm">Kunde erkannt: {foundCustomer.label}</div>
                                </div>
                                <p className="text-[11px] text-white/60 mb-3">Bitte E-Mail & Telefon kurz bestätigen.</p>
                                <div className="space-y-2.5">
                                    <div className="flex items-center gap-2 bg-[#1f2937] border border-white/15 rounded-sm px-3 py-2.5">
                                        <Mail className="w-4 h-4 text-white/40 shrink-0" />
                                        <input value={form.email} onChange={setF("email")} inputMode="email" placeholder="E-Mail" className="bg-transparent flex-1 text-sm outline-none" data-testid="mscan-confirm-email" />
                                    </div>
                                    <div className="flex items-center gap-2 bg-[#1f2937] border border-white/15 rounded-sm px-3 py-2.5">
                                        <Phone className="w-4 h-4 text-white/40 shrink-0" />
                                        <input value={form.phone} onChange={setF("phone")} inputMode="tel" placeholder="Telefon" className="bg-transparent flex-1 text-sm outline-none" data-testid="mscan-confirm-phone" />
                                    </div>
                                </div>
                                <button type="button" onClick={() => setContactConfirmed(true)}
                                    className={`mt-3 w-full rounded-sm py-2.5 text-sm font-bold inline-flex items-center justify-center gap-2 ${contactConfirmed ? "bg-emerald-500 text-white" : "bg-white/10 hover:bg-white/15 text-white"}`}
                                    data-testid="mscan-confirm-contact">
                                    <Check className="w-4 h-4" /> {contactConfirmed ? "Kontaktdaten bestätigt" : "E-Mail & Telefon bestätigen"}
                                </button>
                            </div>
                        )}

                        {/* Schritt 3 — Manuelle Schnellerfassung (letztes Mittel) */}
                        {showManual && (
                            <div className="mt-4 bg-white/5 rounded-sm p-4 space-y-2.5" data-testid="mscan-form">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-[#FFCC00]">Manuelle Schnellerfassung</div>
                                {noMatchAfterScan && (
                                    <div className="text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-sm px-2.5 py-2 flex items-start gap-1.5" data-testid="mscan-nomatch-hint">
                                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                        <span>{foundVehicle
                                            ? "Fahrzeug erkannt, aber kein Kunde zugeordnet. Bitte die Kundendaten kurz erfassen."
                                            : "Beleg gescannt, aber es konnte kein Kunde / Fahrzeug zugeordnet werden. Bitte die Daten kurz manuell erfassen."}</span>
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-2.5">
                                    <input value={form.customer_name} onChange={setF("customer_name")} placeholder="Name" className="col-span-2 bg-[#1f2937] border border-white/15 rounded-sm px-3 py-2.5 text-sm" data-testid="mscan-f-name" />
                                    <input value={form.phone} onChange={setF("phone")} inputMode="tel" placeholder="Telefon" className="bg-[#1f2937] border border-white/15 rounded-sm px-3 py-2.5 text-sm" data-testid="mscan-f-phone" />
                                    <input value={form.email} onChange={setF("email")} inputMode="email" placeholder="E-Mail" className="bg-[#1f2937] border border-white/15 rounded-sm px-3 py-2.5 text-sm" data-testid="mscan-f-email" />
                                    <input value={form.license_plate} onChange={setF("license_plate")} placeholder="Kennzeichen" className="bg-[#1f2937] border border-white/15 rounded-sm px-3 py-2.5 text-sm uppercase" data-testid="mscan-f-plate" />
                                    <input value={form.vin} onChange={setF("vin")} placeholder="FIN (optional)" className="bg-[#1f2937] border border-white/15 rounded-sm px-3 py-2.5 text-sm uppercase" data-testid="mscan-f-vin" />
                                </div>
                            </div>
                        )}

                        {/* Auftragsdaten — Termin, Abstellort, Arbeiten (immer) */}
                        <div className="mt-4 bg-white/5 rounded-sm p-4 space-y-2.5" data-testid="mscan-order-data">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-[#FFCC00]">Auftragsdaten</div>
                            <div className="grid grid-cols-2 gap-2.5">
                                <input type="date" value={form.appointment_date} onChange={setF("appointment_date")} className="bg-[#1f2937] border border-white/15 rounded-sm px-3 py-2.5 text-sm" data-testid="mscan-f-date" />
                                <select value={form.appointment_daypart} onChange={setF("appointment_daypart")} className="bg-[#1f2937] border border-white/15 rounded-sm px-3 py-2.5 text-sm" data-testid="mscan-f-daypart">
                                    <option value="">Tageszeit…</option>
                                    <option value="vormittags">vormittags</option>
                                    <option value="nachmittags">nachmittags</option>
                                    <option value="ganztags">ganztags</option>
                                </select>
                                <input value={form.parking_location} onChange={setF("parking_location")} placeholder="Abstellort (z. B. Hof, Stellplatz 3)" className="col-span-2 bg-[#1f2937] border border-white/15 rounded-sm px-3 py-2.5 text-sm" data-testid="mscan-f-parking" />
                                <textarea value={form.requested_work} onChange={setF("requested_work")} rows={3} placeholder="Notwendige Arbeiten / Mängel (z. B. Inspektion + Bremse vorn)" className="col-span-2 bg-[#1f2937] border border-white/15 rounded-sm px-3 py-2.5 text-sm" data-testid="mscan-f-work" />
                            </div>

                            {/* Belegfotos: KM-Stand immer; Abstellort/Schlüssel zusätzlich außerhalb der Öffnungszeiten */}
                            <div className="grid grid-cols-2 gap-2.5 pt-1" data-testid="mscan-photos">
                                {[
                                    { kind: "kmstand", label: "Foto KM-Stand", icon: Gauge },
                                    ...(outsideHours ? [
                                        { kind: "standort", label: "Abstellort-Foto", icon: MapPin },
                                        { kind: "schluesseleinwurf", label: "Foto Schlüsseleinwurf", icon: KeyRound },
                                    ] : []),
                                ].map(({ kind, label, icon: Icon }) => {
                                    const count = docs.filter((d) => d.kind === kind).length;
                                    const busy = uploadingKind === kind;
                                    return (
                                        <button key={kind} type="button" onClick={() => pick(kind)} disabled={!!uploadingKind}
                                            className="bg-[#1f2937] hover:bg-white/10 border border-white/15 rounded-sm px-3 py-3 flex flex-col items-center gap-1 active:scale-[0.98] transition-transform disabled:opacity-50"
                                            data-testid={`mscan-kind-${kind}`}>
                                            {busy ? <Loader2 className="w-5 h-5 text-[#FFCC00] animate-spin" /> : count > 0 ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <Icon className="w-5 h-5 text-[#FFCC00]" />}
                                            <span className="text-[11px] font-bold text-center leading-tight">{label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {docs.length > 0 && (
                            <div className="mt-4 bg-white/5 rounded-sm p-3" data-testid="mscan-doc-list">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-white/40 mb-1.5">Erfasst ({docs.length})</div>
                                {docs.map((d, i) => (
                                    <div key={i} className="flex items-center gap-2 text-sm py-0.5">
                                        <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                                        <span className="text-white/80 truncate">{LABELS[d.kind] || d.kind}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <button type="button" onClick={finish} disabled={!canFinish || completing || !!uploadingKind}
                            className="mt-6 w-full bg-[#FFCC00] disabled:bg-white/15 disabled:text-white/40 text-[#111827] font-black uppercase tracking-wider rounded-sm py-4 active:scale-95 transition-transform inline-flex items-center justify-center gap-2"
                            data-testid="mscan-finish">
                            {completing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                            {purpose === "tradein" ? "Fertig — an Verkäufer senden" : "Fertig — an Werkstatt senden"}
                        </button>
                            </>
                        )}
                    </div>
                )}

                {state === "done" && (
                    <div className="text-center bg-emerald-500/10 border border-emerald-500/40 rounded-sm px-4 py-8 mt-10" data-testid="mscan-done">
                        <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                        {intakeResult ? (
                            intakeResult.found ? (
                                <>
                                    <div className="font-display text-xl font-black">Fahrzeug erfasst!</div>
                                    <p className="text-sm text-white/80 mt-2">
                                        {[intakeResult.vehicle?.brand, intakeResult.vehicle?.model].filter(Boolean).join(" ") || "Fahrzeug"}
                                    </p>
                                    <div className="mt-3 inline-flex items-center gap-2 bg-[#FFCC00] text-[#111827] font-black rounded-sm px-3 py-1.5" data-testid="mscan-intake-keytag">
                                        <KeyRound className="w-4 h-4" /> Schlüsselnummer: {intakeResult.key_tag || "—"}
                                    </div>
                                    <div className="mt-4 text-left bg-white/5 border border-white/10 rounded-sm divide-y divide-white/10" data-testid="mscan-intake-locations">
                                        {intakeResult.origin?.label && (
                                            <div className="flex items-center gap-2 px-3 py-2.5 text-sm" data-testid="mscan-intake-origin">
                                                <History className="w-4 h-4 text-white/40 shrink-0" />
                                                <span className="text-white/50 w-28 shrink-0">Herkunft</span>
                                                <b className="text-white/90 truncate">{intakeResult.origin.label}</b>
                                            </div>
                                        )}
                                        {intakeResult.previous_location?.label && (
                                            <div className="flex items-center gap-2 px-3 py-2.5 text-sm" data-testid="mscan-intake-previous">
                                                <MapPin className="w-4 h-4 text-white/40 shrink-0" />
                                                <span className="text-white/50 w-28 shrink-0">Vorher</span>
                                                <span className="text-white/80 truncate">{intakeResult.previous_location.label}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2 px-3 py-2.5 text-sm" data-testid="mscan-intake-now">
                                            <MapPin className="w-4 h-4 text-emerald-400 shrink-0" />
                                            <span className="text-white/50 w-28 shrink-0">Jetzt hier</span>
                                            <b className="text-emerald-300 truncate">{intakeResult.branch_label}</b>
                                        </div>
                                    </div>
                                    {intakeResult.duplicate_warning && (
                                        <div className="mt-3 text-[12px] text-red-100 bg-red-600/25 border border-red-500/60 rounded-sm px-3 py-2.5 flex items-start gap-2 text-left" data-testid="mscan-intake-duplicate-done">
                                            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-300" />
                                            <span><b>Hinweis:</b> Das Fahrzeug war zuvor bereits an <b>{intakeResult.duplicate_warning.branch_label || "einem anderen Standort"}</b> eingegangen. Der Eingang wurde auf diesen Standort umgebucht.</span>
                                        </div>
                                    )}
                                    <p className="text-xs text-white/60 mt-3">Die QR-Etiketten werden am Theken-PC gedruckt. Fenster schließen.</p>
                                </>
                            ) : (
                                <>
                                    <div className="font-display text-xl font-black">Beleg erfasst</div>
                                    <p className="text-sm text-white/70 mt-2">FIN {intakeResult.vin || "—"} konnte keinem Bestandsfahrzeug zugeordnet werden. Bitte am Theken-PC prüfen.</p>
                                </>
                            )
                        ) : (
                            <>
                                <div className="font-display text-xl font-black">Fertig!</div>
                                <p className="text-sm text-white/70 mt-2">
                                    Alle Belege wurden erkannt und erscheinen jetzt am Theken-PC.
                                    Du kannst dieses Fenster schließen.
                                </p>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
